import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Activity,
  CreditCard,
  Repeat,
  Package,
  SlidersHorizontal,
  Network,
  Users,
  Globe,
  Waypoints,
  Library,
  Boxes,
  BarChart3,
  Link2,
  ScrollText,
  Menu,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/use-auth';
import type { UserRole } from '@/types/auth';
import { ROLE_GROUPS } from '@/types/auth';
// Devportal-Track Mount-Point (Spec 247 §5): einziger Sidebar-Eingriff des Tracks
import { Code2 } from 'lucide-react';
import { DEVPORTAL_ROLES, isDevportalEnabled } from '@/types/devportal';
import { cn } from '@/lib/utils';
import { Button, Separator, Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui';
import internetqLogo from '@/assets/internetq-logo.png';

interface NavItem {
  path: string;
  labelKey: string;
  icon: typeof LayoutDashboard;
  roles: UserRole[];
}

interface NavSection {
  labelKey: string;
  roles: UserRole[];
  items: NavItem[];
}

/**
 * Navigation sections with role-based access control.
 * Grouped by workflow context: Operations (daily work), Configuration (setup), Platform (infra).
 *
 * @see Spec 110 — Navigation Sektionen umgruppieren (5→3)
 * @see UI Critique Navigation 2026-03-12
 */
const navSections: NavSection[] = [
  {
    labelKey: 'nav.section.operations',
    roles: ROLE_GROUPS.ALL,
    items: [
      {
        path: '/',
        labelKey: 'nav.dashboard',
        icon: LayoutDashboard,
        roles: ROLE_GROUPS.ALL,
      },
      {
        path: '/transactions',
        labelKey: 'nav.transactions',
        icon: CreditCard,
        roles: ROLE_GROUPS.ALL,
      },
      {
        path: '/subscriptions',
        labelKey: 'nav.subscriptions',
        icon: Repeat,
        roles: ROLE_GROUPS.ALL,
      },
      {
        path: '/webhooks',
        labelKey: 'nav.webhookEvents',
        icon: Activity,
        roles: ROLE_GROUPS.WEBHOOK_EVENTS_READ,
      },
      {
        // Spec 429 Increment D: Analytics-Reports (QuickSight-Embed). Label aus dem
        // reports-Namespace (Cross-NS-Key). Sichtbar fuer alle Rollen (REPORTS_READ);
        // die feine reportKey-Isolation erzwingt der Mint-Handler serverseitig.
        path: '/reports',
        labelKey: 'reports:nav.entry',
        icon: BarChart3,
        roles: ROLE_GROUPS.REPORTS_READ,
      },
    ],
  },
  {
    labelKey: 'nav.section.configuration',
    roles: ROLE_GROUPS.ALL,
    items: [
      {
        path: '/merchants',
        labelKey: 'nav.merchants',
        icon: Building2,
        // Spec 0219: Merchants-Registry ist Platform-Sicht — Merchant-Rollen raus.
        roles: ROLE_GROUPS.PLATFORM,
      },
      {
        path: '/services',
        labelKey: 'nav.services',
        icon: Package,
        roles: ROLE_GROUPS.ALL,
      },
      {
        path: '/webhooks/config',
        labelKey: 'nav.webhookConfig',
        icon: SlidersHorizontal,
        roles: ROLE_GROUPS.WEBHOOK_CONFIG_ACCESS,
      },
      {
        // Spec 0221: User-/Team-Management. In der (fuer alle sichtbaren) Konfigurations-
        // Sektion, damit auch merchant_product_manager das Item sieht — die Platform-Sektion
        // ist auf IQ_SERVICES_ACCESS gated und waere fuer Merchants unsichtbar.
        // Spec 525: USER_LIST_VIEW (statt USER_MANAGEMENT), damit platform_ops die
        // /users-Liste im Menue findet (list-only; Detail/Anlegen bleiben gated).
        path: '/users',
        labelKey: 'nav.users',
        icon: Users,
        roles: ROLE_GROUPS.USER_LIST_VIEW,
      },
      {
        // Issue #318: PM-Lese-Ansicht der MNO-IP-Ranges (read-only). In der
        // Konfigurations-Sektion, damit merchant_product_manager den Eintrag sieht.
        path: '/network-coverage',
        labelKey: 'mno-ip-ranges:nav.coverage',
        icon: Globe,
        roles: ROLE_GROUPS.MNO_IP_RANGES_READ,
      },
      {
        // Issue #334: globaler Einstieg zur Asset-Bibliothek. Sichtbar fuer alle
        // Lese-Rollen; Merchant-Rollen sehen nur den eigenen Merchant (Seite waehlt
        // den Merchant aus dem Auth-Context, ohne Picker). Write-Aktionen sind in
        // der Bibliothek selbst per MERCHANT_ASSET_WRITE gegated.
        path: '/assets',
        labelKey: 'nav.assets',
        icon: Library,
        roles: ROLE_GROUPS.MERCHANT_ASSET_READ,
      },
    ],
  },
  {
    labelKey: 'nav.section.platform',
    // Spec 347 §8: Section-Gate auf PLATFORM geweitet (war IQ_SERVICES_ACCESS, ohne
    // platform_ops). Sonst blendet der Section-Filter (filteredSections, unten) die
    // GANZE Sektion fuer platform_ops aus — und das neue OVERVIEW-Item (das
    // platform_ops einschliesst) erschiene nie. Der Item-Filter darunter +
    // Empty-Section-Drop halten Gateway-Config + MNO-IP fuer platform_ops weiterhin
    // verborgen; platform_ops sieht die Sektion dann NUR mit dem Overview-Item.
    roles: ROLE_GROUPS.PLATFORM,
    items: [
      {
        path: '/iq-services',
        labelKey: 'nav.gatewayConfig',
        icon: Network,
        roles: ROLE_GROUPS.IQ_SERVICES_ACCESS,
      },
      {
        // Spec 347 §8: Cross-Merchant-Uebersicht (platform-only, inkl. platform_ops).
        path: '/iq-service-inventory',
        labelKey: 'iq-service-inventory:nav.overview',
        icon: Boxes,
        roles: ROLE_GROUPS.IQ_SERVICE_INVENTORY_OVERVIEW,
      },
      {
        // Issue #318: Platform-Verwaltung der MNO-IP-Ranges.
        path: '/mno-ip-ranges',
        labelKey: 'mno-ip-ranges:nav.platform',
        icon: Waypoints,
        roles: ROLE_GROUPS.MNO_IP_RANGES_ACCESS,
      },
      {
        // Spec 493: Payment-Link Verwaltung (platform-only, read-only in Inc 1).
        path: '/payment-link',
        labelKey: 'payment-link:nav.entry',
        icon: Link2,
        roles: ROLE_GROUPS.PAYMENT_LINK_READ,
      },
      {
        // Spec 528 Phase 6: Audit-Trail-Plattform-Sicht (platform_admin + platform_ops,
        // NICHT platform_it — Item-Gate AUDIT_READ).
        path: '/audit',
        labelKey: 'audit:nav.entry',
        icon: ScrollText,
        roles: ROLE_GROUPS.AUDIT_READ,
      },
    ],
  },
  // Devportal-Track Mount-Point (Spec 247 §5): EIN Eintrag hinter Flag.
  // Labels aus dem devportal-Namespace (Cross-NS-Key) — common.json bleibt
  // unberuehrt. Sichtbarkeit lt. Rollen-Matrix Spec 247 §4.1.
  ...(isDevportalEnabled()
    ? [
        {
          labelKey: 'devportal:nav.section',
          roles: DEVPORTAL_ROLES,
          items: [
            {
              path: '/developer',
              labelKey: 'devportal:nav.entry',
              icon: Code2,
              roles: DEVPORTAL_ROLES,
            },
          ],
        },
      ]
    : []),
];

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

/**
 * Navigation sidebar component.
 * Responsive: Collapsible on desktop, hamburger menu on mobile.
 * Items grouped by domain with section headers.
 *
 * @see Spec 0012 Section 7.1-7.2 - App Shell Layout
 */
/**
 * Determines the `end` prop for NavLink active-state matching.
 * - Dashboard (/): end=true — otherwise it matches every route
 * - /webhooks/config: end=true — exact match to avoid collision with /webhooks
 * - All others: end=false — so detail pages like /transactions/txn_123 highlight the parent
 */
function getNavLinkEnd(path: string): boolean {
  if (path === '/' || path === '/webhooks/config') return true;
  return false;
}

export function Sidebar({ collapsed = false, onToggle }: SidebarProps) {
  const { hasRole } = useAuth();
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Spec 0221: Merchant-Rollen sehen "Team" statt "Benutzer" beim /users-Item.
  const isMerchantUser = hasRole(ROLE_GROUPS.MERCHANT);
  const labelFor = (item: NavItem): string =>
    item.path === '/users' && isMerchantUser ? t('nav.team') : t(item.labelKey);

  /**
   * Custom active check for /webhooks: active when path starts with /webhooks
   * but NOT when it's /webhooks/config (that has its own NavLink).
   */
  const isWebhooksActive = (itemPath: string, isRouterActive: boolean): boolean => {
    if (itemPath === '/webhooks') {
      return location.pathname === '/webhooks' ||
        (location.pathname.startsWith('/webhooks/') && !location.pathname.startsWith('/webhooks/config'));
    }
    return isRouterActive;
  };

  const filteredSections = navSections
    .filter((section) => hasRole(section.roles))
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => hasRole(item.roles)),
    }))
    .filter((section) => section.items.length > 0);

  const navContent = (
    <nav className="flex flex-col gap-0.5 px-2 py-2" role="navigation" aria-label={t('aria.mainNavigation')}>
      {filteredSections.map((section, sectionIndex) => (
        <div key={section.labelKey}>
          {/* Section separator (not before first section) */}
          {sectionIndex > 0 && <Separator className="my-2" />}

          {/* Section header (hidden when collapsed) */}
          {!collapsed && (
            <span className="mb-1 flex items-center gap-1.5 px-3 pt-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <span className="inline-block h-2.5 w-0.5 rounded-full bg-brand-accent" aria-hidden="true" />
              {t(section.labelKey)}
            </span>
          )}

          {/* Section items */}
          {section.items.map((item) => {
            const Icon = item.icon;
            const label = labelFor(item);
            const navLink = (
              <NavLink
                key={item.path}
                to={item.path}
                end={getNavLinkEnd(item.path)}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }: { isActive: boolean }) =>
                  cn(
                    '!flex touch-manipulation items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors coarse:min-h-11',
                    'hover:bg-accent hover:text-accent-foreground',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isWebhooksActive(item.path, isActive) ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
                    collapsed && 'justify-center px-2'
                  )
                }
              >
                <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                {!collapsed && <span>{label}</span>}
              </NavLink>
            );

            if (collapsed) {
              return (
                <TooltipProvider key={item.path} delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>{navLink}</TooltipTrigger>
                    <TooltipContent side="right">{label}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            }

            return navLink;
          })}
        </div>
      ))}
    </nav>
  );

  return (
    <>
      {/* Mobile toggle button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed left-4 top-[1.625rem] z-50 md:hidden"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label={mobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
        aria-expanded={mobileOpen}
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-full border-r bg-card transition-all duration-300',
          'md:relative md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          collapsed ? 'w-14' : 'w-[17rem]'
        )}
        aria-label={t('aria.sidebar')}
      >
        {/* Brand accent line — synced with header stripe */}
        <div className="h-[3px] bg-brand-accent" />

        {/* Logo/Brand — synced with header via CSS var --header-total-height */}
        <div className="flex h-[var(--header-total-height)] items-center justify-center border-b px-3">
          {!collapsed && (
            <div className="flex flex-col items-center gap-1">
              <img
                src={internetqLogo}
                alt="InternetQ"
                className="h-9 w-auto dark:brightness-0 dark:invert"
              />
              <span className="text-[10px] font-medium text-muted-foreground">Admin Panel</span>
            </div>
          )}
          {collapsed && (
            <span className="text-sm font-bold text-brand-accent">iq</span>
          )}
        </div>

        {navContent}

        {/* Collapse toggle (desktop only) */}
        {onToggle && (
          <>
            <Separator className="my-2" />
            <div className="px-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-center coarse:min-h-11"
                onClick={onToggle}
                aria-label={collapsed ? t('aria.expandSidebar') : t('aria.collapseSidebar')}
              >
                <Menu className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
